import React, { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { APP_VERSION } from '../changelog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CHECK_INTERVAL_MS = 2 * 60 * 1000; // 2 minuti

/**
 * VersionWatcher — rileva un nuovo deploy e offre il reload all'utente.
 *
 * Problema risolto: dopo un redeploy, il bundle JS vecchio ancora in memoria
 * nel browser dell'utente può parlare con un backend aggiornato e innescare
 * errori (es. /auth/me risponde con shape diversa → logout a sorpresa).
 *
 * Strategia:
 *  - Polling leggero di GET /api/version ogni 2 minuti e al focus/online.
 *  - Confronta `server_version` con `APP_VERSION` compilata nel bundle.
 *  - Se diverse: toast persistente con pulsante "Aggiorna ora" → hard reload.
 *  - Se l'utente ignora, il toast riappare al prossimo polling (id fisso).
 *  - Nessun reload automatico — l'utente potrebbe stare editando un DDT.
 */
const VersionWatcher = () => {
  const notifiedRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const check = async () => {
      try {
        const r = await axios.get(`${API}/version`, { timeout: 8000 });
        const serverVer = r.data?.version;
        if (!serverVer || serverVer === APP_VERSION) return;
        if (notifiedRef.current) return;
        notifiedRef.current = true;
        toast.info('È disponibile una nuova versione di DDT Manager', {
          id: 'app-version-update',
          duration: Infinity,
          description: `Hai v${APP_VERSION}, è uscita v${serverVer}. Aggiorna per evitare che l'app ti chieda di rifare login.`,
          action: {
            label: 'Aggiorna ora',
            onClick: () => {
              // Pulisce il SW cache e forza una fetch fresca di tutto
              if ('serviceWorker' in navigator) {
                navigator.serviceWorker.getRegistrations().then((regs) => {
                  regs.forEach((reg) => reg.update());
                });
              }
              window.location.reload();
            },
          },
        });
      } catch { /* silent — rete offline, riproveremo */ }
    };

    const firstCheck = setTimeout(check, 5000);
    timerRef.current = setInterval(check, CHECK_INTERVAL_MS);
    const onFocus = () => check();
    const onOnline = () => check();
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onOnline);
    return () => {
      clearTimeout(firstCheck);
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onOnline);
    };
  }, []);

  return null;
};

export default VersionWatcher;
